import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Language } from '../types';
import { api } from '../utils/api';

interface LanguageSearchProps {
  value?: number;
  onChange: (languageId: number | undefined) => void;
  placeholder?: string;
  className?: string;
}

export function LanguageSearch({ value, onChange, placeholder = "Search languages...", className = "" }: LanguageSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Language[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);

  // Load selected language details when value changes
  useEffect(() => {
    if (value && !selectedLanguage) {
      loadLanguageById(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const loadLanguageById = async (languageId: number) => {
    try {
      const response = await api.get(`api/v1/languages/search?q=&per_page=1000`);
      const language = response.data.find((lang: Language) => lang.id === languageId);
      if (language) {
        setSelectedLanguage(language);
      }
    } catch (err) {
      console.error('Failed to load language:', err);
    }
  };

  const searchLanguages = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`api/v1/languages/search?q=${encodeURIComponent(query)}&per_page=50`);
      setSearchResults(response.data);
    } catch (err) {
      console.error('Failed to search languages:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    searchLanguages(query);
  };

  const selectLanguage = (language: Language) => {
    setSelectedLanguage(language);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    onChange(language.id);
  };

  const clearSelection = () => {
    setSelectedLanguage(null);
    onChange(undefined);
  };

  return (
    <div className={`relative ${className}`}>
      {selectedLanguage ? (
        <div className="flex items-center justify-between p-2 border border-gray-300 rounded-md bg-gray-50">
          <span className="text-sm text-gray-900">
            {selectedLanguage.name} ({selectedLanguage.code})
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            placeholder={placeholder}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-8"
          />
          <MagnifyingGlassIcon className="absolute inset-y-0 right-0 pr-2 text-gray-400 pointer-events-none h-4 w-4" />
        </div>
      )}

      {showResults && (searchQuery.length > 0 || loading) && (
        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
          {loading && (
            <div className="p-3 text-sm text-gray-500 text-center">
              Searching...
            </div>
          )}
          
          {!loading && searchResults.length > 0 && (
            <div>
              {searchResults.map((language) => (
                <div
                  key={language.id}
                  className="flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                  onClick={() => selectLanguage(language)}
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {language.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language.code}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {!loading && searchResults.length === 0 && searchQuery.length >= 2 && (
            <div className="p-3 text-sm text-gray-500 text-center">
              No languages found matching "{searchQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
